import {
    CroppedRegion,
    ExternalImageTransferGuardStatus,
    ExternalImageTransferPurpose,
    ImageAnalysisPrivacyFlags,
    ImageSize,
    SelectedRegionCrop,
    SensitiveRegionActionRequired,
    SensitiveRegionCandidate,
    SensitiveRegionDetectionStatus,
    SensitiveRegionKind,
    SensitiveRegionRedactionStatus,
    SensitiveRegionType
} from './domain';
import { ImageNormalizationResult } from './image-normalization-service';

export interface SensitiveRegionDetectionRequest {
    uploadImageId?: string | null;
    localMockImageId?: string | null;
    imageUrl?: string | null;
    imageSize?: ImageSize | null;
    selectedRegion?: CroppedRegion | null;
    cropRegionOriginal?: CroppedRegion | null;
    privacyFlags?: ImageAnalysisPrivacyFlags;
    mockScenario?: string | null;
    redactedCandidateIds?: string[];
    excludedCandidateIds?: string[];
    dismissedCandidateIds?: string[];
}

export interface SensitiveRegionDetectionResponse {
    status: SensitiveRegionDetectionStatus;
    candidates: SensitiveRegionCandidate[];
    needsUserReview: boolean;
    warning: string | null;
    policy: {
        originalExternalTransferAllowed: false;
        clothingCropSeparatedFromSensitiveRegions: true;
        detectionMayFail: true;
        provider: 'mock';
    };
}

export interface SensitiveRegionDetectionAdapter {
    detectSensitiveRegions(request: SensitiveRegionDetectionRequest): Promise<SensitiveRegionDetectionResponse>;
}

export type SensitiveRegionService = SensitiveRegionDetectionAdapter;

export interface ExternalImageTransferGuardInput {
    purpose: ExternalImageTransferPurpose;
    hasExternalTransferConsent?: boolean;
    externalTransferReady?: boolean;
    normalization?: ImageNormalizationResult | null;
    sensitiveDetectionStatus?: SensitiveRegionDetectionStatus | null;
    sensitiveRegionCandidates?: SensitiveRegionCandidate[];
    cropImageId?: string | null;
    cropUrl?: string | null;
    localCropId?: string | null;
    selectedCrop?: SelectedRegionCrop | null;
    requireCrop?: boolean;
}

export interface ExternalImageTransferGuardResult {
    allowed: boolean;
    status: ExternalImageTransferGuardStatus;
    purpose: ExternalImageTransferPurpose;
    blockingCandidateIds: string[];
    message: string;
}

const sensitiveRegionPolicy = {
    originalExternalTransferAllowed: false,
    clothingCropSeparatedFromSensitiveRegions: true,
    detectionMayFail: true,
    provider: 'mock'
} as const;

const completedRedactionStatuses = new Set<SensitiveRegionRedactionStatus>([
    'redacted',
    'excluded',
    'dismissed',
    'not_required'
]);

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function scenarioText(request: SensitiveRegionDetectionRequest): string {
    return [
        request.mockScenario || '',
        request.localMockImageId || '',
        request.uploadImageId || '',
        request.imageUrl || ''
    ].join(' ').toLowerCase();
}

function legacyTypeForKind(kind: SensitiveRegionKind): SensitiveRegionType {
    if (kind === 'dm_text') return 'dm';
    if (kind === 'location_text') return 'location';
    return kind;
}

function redactionStatusForCandidate(
    id: string,
    request: SensitiveRegionDetectionRequest
): SensitiveRegionRedactionStatus {
    if (request.redactedCandidateIds?.includes(id)) return 'redacted';
    if (request.excludedCandidateIds?.includes(id)) return 'excluded';
    if (request.dismissedCandidateIds?.includes(id)) return 'dismissed';
    return 'pending';
}

function legacyActionForRequiredAction(
    actionRequired: SensitiveRegionActionRequired
): SensitiveRegionCandidate['action'] | undefined {
    if (actionRequired === 'blur' || actionRequired === 'review') return actionRequired;
    if (actionRequired === 'exclude_from_external_transfer') return 'exclude_from_crop';
    return undefined;
}

function sensitiveCandidate(input: {
    id: string;
    kind: SensitiveRegionKind;
    boundingBox: CroppedRegion;
    confidence: number;
    source: SensitiveRegionCandidate['source'];
    actionRequired: SensitiveRegionActionRequired;
    request: SensitiveRegionDetectionRequest;
    note?: string;
}): SensitiveRegionCandidate {
    const legacyAction = legacyActionForRequiredAction(input.actionRequired);

    return {
        id: input.id,
        kind: input.kind,
        type: legacyTypeForKind(input.kind),
        confidence: input.confidence,
        boundingBox: cloneData(input.boundingBox),
        source: input.source,
        actionRequired: input.actionRequired,
        redactionStatus: redactionStatusForCandidate(input.id, input.request),
        regionRole: 'sensitive_region',
        note: input.note || null,
        maskUrl: null,
        maskPolygon: null,
        ...(legacyAction ? { action: legacyAction } : {})
    };
}

function baseSnsCaptureCandidates(request: SensitiveRegionDetectionRequest): SensitiveRegionCandidate[] {
    return [
        sensitiveCandidate({
            id: 'sensitive-account-name',
            kind: 'account_name',
            boundingBox: { x: 6, y: 3, width: 42, height: 5 },
            confidence: 0.88,
            source: 'mock',
            actionRequired: 'blur',
            request,
            note: 'SNS 캡처 상단 계정명 후보'
        }),
        sensitiveCandidate({
            id: 'sensitive-face-primary',
            kind: 'face',
            boundingBox: { x: 38, y: 6, width: 24, height: 16 },
            confidence: 0.84,
            source: 'mock',
            actionRequired: 'blur',
            request,
            note: '인물 얼굴 후보'
        }),
        sensitiveCandidate({
            id: 'sensitive-dm-text',
            kind: 'dm_text',
            boundingBox: { x: 9, y: 70, width: 74, height: 16 },
            confidence: 0.8,
            source: 'mock',
            actionRequired: 'blur',
            request,
            note: 'DM 또는 사적 대화 텍스트 후보'
        }),
        sensitiveCandidate({
            id: 'sensitive-location-text',
            kind: 'location_text',
            boundingBox: { x: 8, y: 9, width: 36, height: 5 },
            confidence: 0.76,
            source: 'mock',
            actionRequired: 'blur',
            request,
            note: '위치명 또는 장소 태그 후보'
        })
    ];
}

function bystanderCandidate(request: SensitiveRegionDetectionRequest): SensitiveRegionCandidate {
    return sensitiveCandidate({
        id: 'sensitive-bystander',
        kind: 'bystander',
        boundingBox: { x: 72, y: 18, width: 16, height: 22 },
        confidence: 0.7,
        source: 'mock',
        actionRequired: 'blur',
        request,
        note: '주변인 후보'
    });
}

function unknownTextCandidate(request: SensitiveRegionDetectionRequest): SensitiveRegionCandidate {
    return sensitiveCandidate({
        id: 'sensitive-unknown-text',
        kind: 'unknown_text',
        boundingBox: { x: 12, y: 83, width: 55, height: 7 },
        confidence: 0.58,
        source: 'mock',
        actionRequired: 'review',
        request,
        note: 'OCR로 분류 전 검토가 필요한 텍스트 후보'
    });
}

export function requiresRedactionBeforeExternalTransfer(candidates: SensitiveRegionCandidate[] = []): boolean {
    return candidates.some((candidate) => (
        candidate.actionRequired !== 'none' &&
        !completedRedactionStatuses.has(candidate.redactionStatus)
    ));
}

function imageReadyForExternalTransfer(input: ExternalImageTransferGuardInput): boolean {
    if (typeof input.externalTransferReady === 'boolean') return input.externalTransferReady;
    if (!input.normalization) return false;
    return Boolean(
        input.normalization.ok &&
        input.normalization.metadata.externalTransferReady &&
        input.normalization.exifStripped &&
        input.normalization.orientationHandled
    );
}

function hasCropForExternalTransfer(input: ExternalImageTransferGuardInput): boolean {
    if (input.requireCrop === false) return true;
    return Boolean(
        input.cropImageId ||
        input.cropUrl ||
        input.localCropId ||
        input.selectedCrop?.crop_image_id ||
        input.selectedCrop?.crop_url ||
        input.selectedCrop?.local_crop_id
    );
}

export function guardExternalImageTransfer(input: ExternalImageTransferGuardInput): ExternalImageTransferGuardResult {
    if (!input.hasExternalTransferConsent) {
        return {
            allowed: false,
            status: 'blocked_external_consent_required',
            purpose: input.purpose,
            blockingCandidateIds: [],
            message: '외부 AI/embedding 전송은 사용자의 명시 동의 전에는 진행할 수 없습니다.'
        };
    }

    if (!imageReadyForExternalTransfer(input)) {
        return {
            allowed: false,
            status: 'blocked_not_normalized',
            purpose: input.purpose,
            blockingCandidateIds: [],
            message: 'EXIF 제거와 orientation 정규화가 끝난 이미지 또는 crop만 외부 전송할 수 있습니다.'
        };
    }

    if (input.sensitiveDetectionStatus === 'failed') {
        return {
            allowed: false,
            status: 'blocked_sensitive_detection_failed',
            purpose: input.purpose,
            blockingCandidateIds: [],
            message: '민감 영역 감지가 실패해 원본 또는 미검토 crop 외부 전송을 막았습니다.'
        };
    }

    if (input.sensitiveDetectionStatus !== 'detected' && input.sensitiveDetectionStatus !== 'not_detected') {
        return {
            allowed: false,
            status: 'blocked_sensitive_detection_required',
            purpose: input.purpose,
            blockingCandidateIds: [],
            message: '민감 영역 감지 결과가 확인되기 전에는 실제 provider로 이미지를 전송할 수 없습니다.'
        };
    }

    const blockingCandidates = (input.sensitiveRegionCandidates || []).filter((candidate) => (
        candidate.actionRequired !== 'none' &&
        !completedRedactionStatuses.has(candidate.redactionStatus)
    ));
    if (blockingCandidates.length > 0) {
        return {
            allowed: false,
            status: 'blocked_until_redacted',
            purpose: input.purpose,
            blockingCandidateIds: blockingCandidates.map((candidate) => candidate.id),
            message: '민감 영역 후보가 블러 또는 외부 전송 제외 처리될 때까지 외부 분석 요청을 보류합니다.'
        };
    }

    if (!hasCropForExternalTransfer(input)) {
        return {
            allowed: false,
            status: 'blocked_crop_required',
            purpose: input.purpose,
            blockingCandidateIds: [],
            message: '실제 provider 전송은 원본 전체 이미지가 아니라 사용자가 확정한 의류 crop이 있을 때만 가능합니다.'
        };
    }

    return {
        allowed: true,
        status: 'allowed',
        purpose: input.purpose,
        blockingCandidateIds: [],
        message: '외부 전송 전 민감 영역 후보 검토가 완료되었습니다.'
    };
}

export class MockSensitiveRegionDetectionAdapter implements SensitiveRegionDetectionAdapter {
    async detectSensitiveRegions(request: SensitiveRegionDetectionRequest): Promise<SensitiveRegionDetectionResponse> {
        const scenario = scenarioText(request);
        if (/sensitive[-_ ]?detection[-_ ]?failed|redaction[-_ ]?provider[-_ ]?fail|privacy[-_ ]?scan[-_ ]?fail/.test(scenario)) {
            return {
                status: 'failed',
                candidates: [],
                needsUserReview: true,
                warning: '민감 영역 감지가 실패할 수 있으므로 사용자가 원본과 선택 영역을 확인해야 합니다.',
                policy: sensitiveRegionPolicy
            };
        }

        if (/no[-_ ]?sensitive|catalog|plain[-_ ]?product/.test(scenario)) {
            return {
                status: 'not_detected',
                candidates: [],
                needsUserReview: false,
                warning: null,
                policy: sensitiveRegionPolicy
            };
        }

        const shouldMockSnsSensitiveRegions = Boolean(
            request.privacyFlags?.containsSensitiveContext ||
            /sns|dm|capture|screenshot|instagram|tiktok|account|location|private/.test(scenario)
        );
        if (!shouldMockSnsSensitiveRegions) {
            return {
                status: 'not_detected',
                candidates: [],
                needsUserReview: false,
                warning: null,
                policy: sensitiveRegionPolicy
            };
        }

        const candidates = baseSnsCaptureCandidates(request);
        if (/bystander|crowd|street/.test(scenario)) {
            candidates.push(bystanderCandidate(request));
        }
        if (/unknown[-_ ]?text|ocr|caption/.test(scenario)) {
            candidates.push(unknownTextCandidate(request));
        }

        return {
            status: candidates.length > 0 ? 'detected' : 'not_detected',
            candidates,
            needsUserReview: candidates.some((candidate) => candidate.redactionStatus === 'pending'),
            warning: '목업 후보입니다. 실제 OCR/face detection은 실패하거나 오탐할 수 있어 외부 전송 전 사용자 확인이 필요합니다.',
            policy: sensitiveRegionPolicy
        };
    }
}

export const mockSensitiveRegionService: SensitiveRegionService = new MockSensitiveRegionDetectionAdapter();
